
import { RiderFile } from '../types';

/**
 * Safely opens a file in a new tab or triggers a download.
 * Specifically handles Data URLs by converting them to Blobs first.
 * This prevents "Not allowed to navigate top frame to data URL" errors.
 */
export const openAttachment = (file: RiderFile) => {
    if (!file.url) return;

    // Check if it's a Data URL (starts with data:)
    if (file.url.startsWith('data:')) {
        try {
            const parts = file.url.split(',');
            if (parts.length < 2) throw new Error("Invalid Data URL format");

            const mimeMatch = parts[0].match(/:(.*?);/);
            const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
            
            // Decode base64 string
            const b64Data = parts[1].replace(/\s/g, ''); // Remove any whitespace
            const byteCharacters = atob(b64Data);
            const byteArrays = [];

            for (let offset = 0; offset < byteCharacters.length; offset += 512) {
                const slice = byteCharacters.slice(offset, offset + 512);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }

            const blob = new Blob(byteArrays, { type: mime });
            const blobUrl = URL.createObjectURL(blob);
            
            // Open in a new window/tab
            // For images, browsers will render them directly. For PDFs, they use the internal viewer.
            const newWindow = window.open(blobUrl, '_blank');
            
            if (newWindow) {
                newWindow.focus();
                // We keep the URL alive while the window is open. 
                // Since we can't reliably know when the tab closes, we let the browser clean it up
                // or revoke after a long delay if needed. Usually, for simple "popups", this is fine.
            } else {
                // Fallback for popup blockers: trigger a download
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = file.name;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
            
        } catch (error) {
            console.error("Critical error processing attachment:", error);
            alert("This file could not be opened. It might be corrupted or too large for your browser.");
        }
    } else {
        // Normal HTTPS URL: simple window.open is fine
        window.open(file.url, '_blank', 'noopener,noreferrer');
    }
};
